import { MODES, type Character, type Challenge, type ModeId, type World } from './catalog';
import { playPageChime, playTone } from './audio';
import type { SaveData } from './storage';
import { CHARACTER_ATLAS_PATH, characterAtlasFrame, publicAsset, SWIPE_ITEM_ART_PATHS, WORLD_ART_PATHS } from './art';

const W = 390;
const H = 700;
const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));
const rand = (min: number, max: number): number => min + Math.random() * (max - min);

export interface SessionConfig {
  world: World;
  character: Character;
  mode: ModeId;
  challenge?: Challenge;
  save: SaveData;
  onProgress: (progress: GameProgress) => void;
  onFinish: (result: GameResult) => void;
}

export interface GameProgress {
  score: number;
  objectiveProgress: number;
  pages: number;
  time: number;
  combo: number;
  lives: number;
}

export interface GameResult extends GameProgress {
  won: boolean;
}

interface FlightObstacle { x: number; width: number; gapY: number; gap: number; passed: boolean; }
interface FallPlatform { x: number; y: number; width: number; moving: boolean; phase: number; page: boolean; }
interface SwipeObject { x: number; y: number; vx: number; vy: number; r: number; kind: 'target' | 'hazard' | 'page'; color: string; label: string; sliced: boolean; }
interface Lane { safe: boolean; direction: number; speed: number; obstacles: { x: number; width: number }[]; pageX?: number; }
interface Particle { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number; }

export class ArcadeSession {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: SessionConfig;
  private worldImage?: HTMLImageElement;
  private characterAtlas?: HTMLImageElement;
  private swipeTargetImage?: HTMLImageElement;
  private swipeHazardImage?: HTMLImageElement;
  private raf = 0;
  private last = 0;
  private elapsed = 0;
  private hudClock = 0;
  private finished = false;
  private paused = false;
  private score = 0;
  private pages = 0;
  private combo = 0;
  private lives = 3;
  private particles: Particle[] = [];
  private pointerStart: { x: number; y: number; time: number } | null = null;
  private pointerLast: { x: number; y: number } | null = null;
  private direction = 0;
  private directionKeys = new Set<string>();
  private touchDirection = 0;
  private swipeTrail: { x: number; y: number; life: number }[] = [];
  private game: Record<string, unknown> = {};
  private resizeObserver: ResizeObserver;

  constructor(canvas: HTMLCanvasElement, config: SessionConfig) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.config = config;
    this.lives = config.mode === 'swipe' ? 5 : 3;
    if (typeof document !== 'undefined' && typeof Image !== 'undefined') {
      this.worldImage = new Image();
      this.worldImage.src = publicAsset(WORLD_ART_PATHS[config.world.id]);
      this.characterAtlas = new Image();
      this.characterAtlas.src = publicAsset(CHARACTER_ATLAS_PATH);
      if (config.mode === 'swipe') {
        const itemArt = SWIPE_ITEM_ART_PATHS[config.world.id];
        this.swipeTargetImage = new Image();
        this.swipeTargetImage.src = publicAsset(itemArt.target);
        this.swipeHazardImage = new Image();
        this.swipeHazardImage.src = publicAsset(itemArt.hazard);
      }
    }
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.setupMode();
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.raf = requestAnimationFrame(this.frame);
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  pause(): void {
    this.paused = true;
    this.pointerStart = null;
    this.pointerLast = null;
    this.touchDirection = 0;
    this.directionKeys.clear();
  }

  resume(): void {
    this.paused = false;
    this.last = 0;
  }

  isPaused(): boolean {
    return this.paused;
  }

  setDirection(direction: -1 | 0 | 1): void {
    if (!this.paused) this.touchDirection = direction;
  }

  moveCrossing(direction: 'up' | 'down' | 'left' | 'right'): void {
    if (this.config.mode !== 'crossing' || this.finished || this.paused) return;
    const game = this.game;
    const player = game.player as { col: number; row: number };
    if (direction === 'left') player.col = Math.max(0, player.col - 1);
    if (direction === 'right') player.col = Math.min(7, player.col + 1);
    if (direction === 'down') player.row = Math.min(11, player.row + 1);
    if (direction === 'up') {
      if (player.row <= 2) {
        game.lanes = [this.makeLane(), ...(game.lanes as Lane[]).slice(0, 11)];
        player.row = 2;
        this.score += 1;
      } else {
        player.row -= 1;
      }
    }
    const lane = (game.lanes as Lane[])[player.row];
    const px = 39 + player.col * 44.5;
    if (lane?.pageX !== undefined && Math.abs(px - lane.pageX) < 22) {
      lane.pageX = undefined;
      this.collectPage(px, 110 + player.row * 48);
    }
    if (lane && !lane.safe && lane.obstacles.some((obstacle) => px + 12 > obstacle.x && px - 12 < obstacle.x + obstacle.width)) {
      this.finish(false);
      return;
    }
    playTone(this.config.save, 430, 0.035, 'triangle');
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.ctx.setTransform(dpr * rect.width / W, 0, 0, dpr * rect.height / H, 0, 0);
  }

  private setupMode(): void {
    switch (this.config.mode) {
      case 'flight':
        this.game = { player: { x: 96, y: 350, vy: 0, r: 15 }, obstacles: [] as FlightObstacle[], spawn: 0.5, pages: [] as { x: number; y: number }[] };
        break;
      case 'fall': {
        const platforms: FallPlatform[] = [
          { x: 97, y: 560, width: 196, moving: false, phase: 0, page: false },
          { x: 140, y: 445, width: 110, moving: false, phase: 0, page: true },
          { x: 170, y: 330, width: 112, moving: true, phase: 1.1, page: false },
          { x: 135, y: 215, width: 100, moving: false, phase: 0, page: true },
          { x: 180, y: 100, width: 95, moving: true, phase: 2.2, page: false },
        ];
        this.game = { player: { x: 195, y: 520, vx: 0, vy: 0, r: 15 }, platforms, hazards: [] as { x: number; y: number; r: number }[], height: 0, topY: -15 };
        break;
      }
      case 'swipe':
        this.game = { objects: [] as SwipeObject[], spawn: 0.45, targetCount: 0 };
        break;
      case 'crossing':
        this.game = {
          player: { col: 4, row: 10 },
          lanes: Array.from({ length: 12 }, (_, index) => index > 8 ? { safe: true, direction: 0, speed: 0, obstacles: [] } : this.makeLane()),
        };
        break;
    }
  }

  private makeLane(): Lane {
    const safe = Math.random() < 0.29;
    const direction = Math.random() < 0.5 ? -1 : 1;
    const speed = rand(45, 95) * direction;
    const count = Math.random() < 0.55 ? 2 : 1;
    const obstacles = Array.from({ length: count }, (_, index) => ({
      x: index === 0 ? rand(-30, 285) : rand(110, 360),
      width: rand(34, 55),
    }));
    return { safe, direction, speed, obstacles, ...(Math.random() < 0.26 ? { pageX: rand(48, 350) } : {}) };
  }

  private frame = (timestamp: number): void => {
    if (this.paused) {
      this.last = timestamp;
      this.raf = requestAnimationFrame(this.frame);
      return;
    }
    if (!this.last) this.last = timestamp;
    const dt = Math.min((timestamp - this.last) / 1000, 0.04);
    this.last = timestamp;
    if (!this.finished) {
      this.elapsed += dt;
      this.hudClock += dt;
      this.update(dt);
      if (this.hudClock > 0.14) {
        this.hudClock = 0;
        this.config.onProgress({ score: this.score, objectiveProgress: this.objectiveProgress(), pages: this.pages, time: this.elapsedSeconds(), combo: this.combo, lives: this.lives });
      }
    }
    this.draw();
    this.raf = requestAnimationFrame(this.frame);
  };

  private elapsedSeconds(): number {
    return Math.floor(this.elapsed);
  }

  private objectiveProgress(): number {
    if (this.config.mode === 'fall') return Math.floor(Number(this.game.height));
    if (this.config.mode === 'swipe') return Number(this.game.targetCount);
    return this.score;
  }

  private update(dt: number): void {
    this.updateParticles(dt);
    if (this.config.mode === 'flight') this.updateFlight(dt);
    if (this.config.mode === 'fall') this.updateFall(dt);
    if (this.config.mode === 'swipe') this.updateSwipe(dt);
    if (this.config.mode === 'crossing') this.updateCrossing(dt);
    this.swipeTrail = this.swipeTrail.map((dot) => ({ ...dot, life: dot.life - dt })).filter((dot) => dot.life > 0);
    this.tryCompleteChallenge();
  }

  private tryCompleteChallenge(): void {
    const challenge = this.config.challenge;
    if (!challenge || this.elapsed < challenge.duration) return;
    const progress = this.config.mode === 'fall' ? Number(this.game.height)
      : this.config.mode === 'swipe' ? Number(this.game.targetCount)
        : this.score;
    if (progress >= challenge.target) this.finish(true);
  }

  private updateFlight(dt: number): void {
    const player = this.game.player as { x: number; y: number; vy: number; r: number };
    const obstacles = this.game.obstacles as FlightObstacle[];
    const pages = this.game.pages as { x: number; y: number }[];
    player.vy += 850 * dt;
    player.y += player.vy * dt;
    this.game.spawn = Number(this.game.spawn) - dt;
    if (Number(this.game.spawn) <= 0) {
      const gap = Math.max(156, 190 - Math.min(this.score, 28) * 1.2);
      const gapY = rand(166 + gap / 2, 548 - gap / 2);
      obstacles.push({ x: 420, width: 68, gapY, gap, passed: false });
      if (Math.random() < 0.72) pages.push({ x: 458, y: gapY + rand(-35, 35) });
      this.game.spawn = Math.max(1.28, 1.5 - this.score * 0.004);
    }
    const speed = Math.min(270, 178 + this.score * 2);
    for (const obstacle of obstacles) {
      obstacle.x -= speed * dt;
      const inX = player.x + player.r > obstacle.x && player.x - player.r < obstacle.x + obstacle.width;
      if (inX && (player.y - player.r < obstacle.gapY - obstacle.gap / 2 || player.y + player.r > obstacle.gapY + obstacle.gap / 2)) {
        this.finish(false);
        return;
      }
      if (!obstacle.passed && obstacle.x + obstacle.width < player.x) {
        obstacle.passed = true;
        this.score += 1;
        this.combo += 1;
        playTone(this.config.save, 520 + Math.min(this.combo, 10) * 28, 0.035, 'triangle');
      }
    }
    this.game.obstacles = obstacles.filter((obstacle) => obstacle.x > -100);
    for (const page of pages) {
      page.x -= speed * dt;
      if (Math.hypot(player.x - page.x, player.y - page.y) < 31) {
        page.x = -100;
        this.collectPage(player.x, player.y);
      }
    }
    this.game.pages = pages.filter((page) => page.x > -30);
    if (player.y - player.r < 46 || player.y + player.r > 666) this.finish(false);
  }

  private updateFall(dt: number): void {
    const player = this.game.player as { x: number; y: number; vx: number; vy: number; r: number };
    const platforms = this.game.platforms as FallPlatform[];
    const hazards = this.game.hazards as { x: number; y: number; r: number }[];
    const keyDirection = (this.directionKeys.has('ArrowLeft') || this.directionKeys.has('a') ? -1 : 0) + (this.directionKeys.has('ArrowRight') || this.directionKeys.has('d') ? 1 : 0);
    this.direction = keyDirection || this.touchDirection;
    player.vx += ((this.direction * 255) - player.vx) * Math.min(1, dt * 7);
    player.x += player.vx * dt;
    if (player.x < -10) player.x = W + 8;
    if (player.x > W + 10) player.x = -8;
    const oldBottom = player.y + player.r;
    player.vy += 1070 * dt;
    player.y += player.vy * dt;
    for (const platform of platforms) {
      if (platform.moving) platform.x += Math.sin(this.elapsed * 1.5 + platform.phase) * 24 * dt;
      if (player.vy > 0 && oldBottom <= platform.y && player.y + player.r >= platform.y && player.x > platform.x - 12 && player.x < platform.x + platform.width + 12) {
        player.y = platform.y - player.r;
        player.vy = -590;
        this.combo = Math.min(99, this.combo + 1);
        if (platform.page) {
          platform.page = false;
          this.collectPage(player.x, platform.y - 24);
        }
        playTone(this.config.save, 360 + this.combo * 10, 0.045, 'sine');
      }
    }
    if (player.y < 285) {
      const shift = 285 - player.y;
      player.y = 285;
      this.game.height = Number(this.game.height) + shift;
      this.score = Math.floor(Number(this.game.height) / 38);
      for (const platform of platforms) platform.y += shift;
      for (const hazard of hazards) hazard.y += shift;
      while (Math.min(...platforms.map((platform) => platform.y)) > -80) {
        const top = Math.min(...platforms.map((platform) => platform.y));
        const last = platforms.find((platform) => platform.y === top)!;
        const width = clamp(112 - Number(this.game.height) / 260, 72, 112);
        const x = clamp(last.x + rand(-125, 125), 18, W - width - 18);
        platforms.push({ x, y: top - rand(104, 128), width, moving: Math.random() < 0.33, phase: rand(0, 6), page: Math.random() < 0.42 });
        const hazardChance = 0.045 + Math.min(Number(this.game.height) / 100000, 0.06);
        if (Math.random() < hazardChance) hazards.push({ x: rand(34, 350), y: top - 65, r: 20 });
      }
    }
    for (const hazard of hazards) {
      if (Math.hypot(hazard.x - player.x, hazard.y - player.y) < hazard.r + player.r - 3) {
        this.finish(false);
        return;
      }
    }
    this.game.platforms = platforms.filter((platform) => platform.y < H + 100);
    this.game.hazards = hazards.filter((hazard) => hazard.y < H + 60);
    if (player.y > H + 35) this.finish(false);
  }

  private updateSwipe(dt: number): void {
    const objects = this.game.objects as SwipeObject[];
    this.game.spawn = Number(this.game.spawn) - dt;
    if (Number(this.game.spawn) <= 0) {
      const count = Math.random() < 0.35 ? 2 : 1;
      for (let index = 0; index < count; index += 1) {
        const roll = Math.random();
        const kind: SwipeObject['kind'] = roll < 0.16 ? 'hazard' : (roll < 0.36 ? 'page' : 'target');
        const label = kind === 'hazard' ? this.config.world.hazards[Math.floor(Math.random() * this.config.world.hazards.length)] : this.config.world.safeObjects[Math.floor(Math.random() * this.config.world.safeObjects.length)];
        objects.push({ x: rand(48, 342), y: 740, vx: rand(-120, 120), vy: rand(-660, -520), r: kind === 'page' ? 15 : rand(22, 30), kind, color: kind === 'hazard' ? '#d97879' : (kind === 'page' ? '#f3d477' : this.config.world.palette[2]), label, sliced: false });
      }
      this.game.spawn = rand(0.72, 1.02);
    }
    for (const object of objects) {
      object.vy += 420 * dt;
      object.x += object.vx * dt;
      object.y += object.vy * dt;
    }
    this.game.objects = objects.filter((object) => object.y < 770 && !object.sliced);
  }

  private updateCrossing(dt: number): void {
    const lanes = this.game.lanes as Lane[];
    for (const lane of lanes) {
      for (const obstacle of lane.obstacles) {
        obstacle.x += lane.speed * dt;
        if (lane.speed > 0 && obstacle.x > 425) obstacle.x = -obstacle.width - rand(10, 90);
        if (lane.speed < 0 && obstacle.x < -obstacle.width - 5) obstacle.x = 425 + rand(10, 90);
      }
    }
    const player = this.game.player as { col: number; row: number };
    const lane = lanes[player.row];
    const px = 39 + player.col * 44.5;
    if (lane && !lane.safe && lane.obstacles.some((obstacle) => px + 12 > obstacle.x && px - 12 < obstacle.x + obstacle.width)) {
      this.finish(false);
      return;
    }
  }

  private collectPage(x: number, y: number): void {
    this.pages += 1;
    this.combo += 1;
    this.emitParticles(x, y, this.config.world.palette[2], 10);
    playPageChime(this.config.save);
  }

  private finish(won: boolean): void {
    if (this.finished) return;
    this.finished = true;
    const time = this.elapsedSeconds();
    const objectiveProgress = this.objectiveProgress();
    this.config.onProgress({ score: this.score, objectiveProgress, pages: this.pages, time, combo: this.combo, lives: this.lives });
    this.config.onFinish({ score: this.score, objectiveProgress, pages: this.pages, time, combo: this.combo, lives: this.lives, won });
  }

  private pointerPosition(event: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * W / rect.width, y: (event.clientY - rect.top) * H / rect.height };
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (this.paused || this.finished) return;
    this.canvas.setPointerCapture(event.pointerId);
    const position = this.pointerPosition(event);
    this.pointerStart = { ...position, time: this.elapsed };
    this.pointerLast = position;
    if (this.config.mode === 'flight') this.flap();
    if (this.config.mode === 'fall') this.touchDirection = position.x < W / 2 ? -1 : 1;
    if (this.config.mode === 'swipe') this.swipeTrail.push({ ...position, life: 0.24 });
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (this.paused || this.finished) return;
    const position = this.pointerPosition(event);
    if (this.config.mode === 'swipe' && this.pointerLast) this.checkSwipeSegment(this.pointerLast, position);
    if (this.config.mode === 'fall' && this.pointerStart) this.touchDirection = position.x < W / 2 ? -1 : 1;
    if (this.config.mode === 'swipe') this.swipeTrail.push({ ...position, life: 0.24 });
    this.pointerLast = position;
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.paused || this.finished) return;
    const position = this.pointerPosition(event);
    if (this.config.mode === 'crossing' && this.pointerStart) {
      const dx = position.x - this.pointerStart.x;
      const dy = position.y - this.pointerStart.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 18) {
        if (Math.abs(dx) > Math.abs(dy)) this.moveCrossing(dx > 0 ? 'right' : 'left');
        else this.moveCrossing(dy > 0 ? 'down' : 'up');
      }
    }
    this.pointerStart = null;
    this.pointerLast = null;
    this.touchDirection = 0;
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.paused || this.finished) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(event.key)) event.preventDefault();
    if (this.config.mode === 'flight' && (event.key === ' ' || event.key === 'ArrowUp')) this.flap();
    if (this.config.mode === 'fall' && ['ArrowLeft', 'ArrowRight', 'a', 'd'].includes(key)) this.directionKeys.add(key);
    if (this.config.mode === 'crossing') {
      if (['ArrowUp', 'w'].includes(key)) this.moveCrossing('up');
      if (['ArrowDown', 's'].includes(key)) this.moveCrossing('down');
      if (['ArrowLeft', 'a'].includes(key)) this.moveCrossing('left');
      if (['ArrowRight', 'd'].includes(key)) this.moveCrossing('right');
    }
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    this.directionKeys.delete(key);
  };

  private flap(): void {
    if (this.config.mode !== 'flight' || this.finished) return;
    (this.game.player as { vy: number }).vy = -318;
    this.combo = 0;
    playTone(this.config.save, 410, 0.045, 'triangle');
  }

  private checkSwipeSegment(from: { x: number; y: number }, to: { x: number; y: number }): void {
    const objects = this.game.objects as SwipeObject[];
    for (const object of objects) {
      if (object.sliced) continue;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const lengthSquared = dx * dx + dy * dy || 1;
      const t = clamp(((object.x - from.x) * dx + (object.y - from.y) * dy) / lengthSquared, 0, 1);
      const nearX = from.x + t * dx;
      const nearY = from.y + t * dy;
      if (Math.hypot(object.x - nearX, object.y - nearY) < object.r + 13) {
        object.sliced = true;
        if (object.kind === 'hazard') {
          this.lives -= 1;
          this.combo = 0;
          this.emitParticles(object.x, object.y, '#ef777b', 12);
          playTone(this.config.save, 190, 0.12, 'sawtooth');
          if (this.lives <= 0) this.finish(false);
        } else if (object.kind === 'page') {
          this.collectPage(object.x, object.y);
          this.score += 1;
        } else {
          this.score += 1;
          this.combo += 1;
          this.game.targetCount = Number(this.game.targetCount) + 1;
          this.emitParticles(object.x, object.y, object.color, 9);
          playTone(this.config.save, 590 + Math.min(this.combo, 12) * 22, 0.06, 'sine');
        }
      }
    }
  }

  private emitParticles(x: number, y: number, color: string, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = rand(45, 170);
      this.particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: rand(0.25, 0.65), color, size: rand(2, 5) });
    }
  }

  private updateParticles(dt: number): void {
    this.particles = this.particles.map((particle) => ({ ...particle, x: particle.x + particle.vx * dt, y: particle.y + particle.vy * dt, vy: particle.vy + 100 * dt, life: particle.life - dt })).filter((particle) => particle.life > 0);
  }

  private draw(): void {
    const ctx = this.ctx;
    this.drawWorldBackground();
    this.drawScenery();
    if (this.config.mode === 'flight') this.drawFlight();
    if (this.config.mode === 'fall') this.drawFall();
    if (this.config.mode === 'swipe') this.drawSwipe();
    if (this.config.mode === 'crossing') this.drawCrossing();
    this.drawParticles();
    if (this.config.mode === 'swipe') this.drawTrail();
    if (this.elapsed < 3.5) {
      const mode = MODES.find((item) => item.id === this.config.mode)!;
      ctx.fillStyle = 'rgba(13, 18, 30, .62)';
      ctx.beginPath(); ctx.roundRect(24, 82, W - 48, 44, 14); ctx.fill();
      ctx.fillStyle = '#fff6dc'; ctx.font = '600 13px system-ui'; ctx.textAlign = 'center'; ctx.fillText(mode.controls, W / 2, 109);
    }
  }

  private drawWorldBackground(): void {
    const ctx = this.ctx;
    const fallback = ctx.createLinearGradient(0, 0, 0, H);
    fallback.addColorStop(0, this.config.world.palette[0]);
    fallback.addColorStop(0.62, this.config.world.palette[1]);
    fallback.addColorStop(1, '#151c2b');
    ctx.fillStyle = fallback;
    ctx.fillRect(0, 0, W, H);

    if (this.worldImage?.complete && this.worldImage.naturalWidth > 0) {
      const image = this.worldImage;
      const scale = Math.max(W / image.naturalWidth, H / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      ctx.drawImage(image, (W - width) / 2, (H - height) / 2, width, height);
      ctx.fillStyle = 'rgba(9, 14, 27, .19)';
      ctx.fillRect(0, 0, W, H);
    }

    const shade = ctx.createLinearGradient(0, 0, 0, H);
    shade.addColorStop(0, 'rgba(9, 13, 25, .34)');
    shade.addColorStop(0.2, 'rgba(9, 13, 25, .07)');
    shade.addColorStop(0.76, 'rgba(9, 13, 25, .07)');
    shade.addColorStop(1, 'rgba(9, 13, 25, .31)');
    ctx.fillStyle = shade;
    ctx.fillRect(0, 0, W, H);
  }

  private drawScenery(): void {
    const ctx = this.ctx;
    const [,,glow] = this.config.world.palette;
    const vignette = ctx.createRadialGradient(W / 2, H * 0.46, 145, W / 2, H * 0.46, 430);
    vignette.addColorStop(0, 'rgba(7, 11, 21, 0)');
    vignette.addColorStop(1, 'rgba(7, 11, 21, .24)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 0.24;
    for (let i = 0; i < 8; i += 1) {
      const x = (i * 91 + 37) % W;
      const y = (i * 139 + this.elapsed * (7 + i % 3)) % H;
      ctx.fillStyle = glow;
      this.drawStar(x, y, i % 3 === 0 ? 3.5 : 1.7);
    }
    ctx.globalAlpha = 1;
  }

  private drawFlight(): void {
    const ctx = this.ctx;
    const player = this.game.player as { x: number; y: number; vy: number; r: number };
    const obstacles = this.game.obstacles as FlightObstacle[];
    const pages = this.game.pages as { x: number; y: number }[];
    for (const obstacle of obstacles) {
      const topHeight = obstacle.gapY - obstacle.gap / 2;
      const bottomY = obstacle.gapY + obstacle.gap / 2;
      this.drawGate(obstacle.x, 40, obstacle.width, topHeight - 40);
      this.drawGate(obstacle.x, bottomY, obstacle.width, H - 40 - bottomY);
    }
    for (const page of pages) this.drawPage(page.x, page.y, 14);
    this.drawHero(player.x, player.y, this.config.character, 18, Math.max(-0.35, Math.min(0.35, player.vy / 1100)));
    ctx.fillStyle = 'rgba(255,255,255,.12)';
    ctx.fillRect(20, 48, W - 40, 2);
  }

  private drawGate(x: number, y: number, width: number, height: number): void {
    if (height <= 0) return;
    const ctx = this.ctx;
    ctx.fillStyle = 'rgba(15, 22, 35, .52)';
    ctx.beginPath(); ctx.roundRect(x - 5, y, width + 10, height, 11); ctx.fill();
    ctx.fillStyle = this.config.world.palette[2];
    ctx.globalAlpha = 0.62;
    ctx.beginPath(); ctx.roundRect(x, y, width, height, 8); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,.24)';
    ctx.fillRect(x + 8, y + 4, 5, Math.max(0, height - 8));
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, width, height); ctx.clip();
    ctx.globalAlpha = 0.2;
    ctx.strokeStyle = '#fff4d1';
    ctx.fillStyle = '#fff4d1';
    if (this.config.world.id === 'looking-glass') {
      for (let row = 0; row < Math.ceil(height / 20); row += 1) for (let col = 0; col < 3; col += 1) {
        if ((row + col) % 2 === 0) ctx.fillRect(x + col * width / 3, y + row * 20, width / 3, 20);
      }
    } else if (this.config.world.id === 'dracula') {
      for (let i = 0; i < Math.max(1, Math.floor(height / 28)); i += 1) {
        ctx.beginPath(); ctx.moveTo(x + i * 24, y); ctx.lineTo(x + i * 24 + 12, y - 10); ctx.lineTo(x + i * 24 + 24, y); ctx.fill();
      }
    } else if (this.config.world.id === 'frankenstein') {
      for (let i = 0; i < Math.max(1, Math.floor(height / 38)); i += 1) {
        const yy = y + i * 38;
        ctx.beginPath(); ctx.moveTo(x + 11, yy); ctx.lineTo(x + 27, yy + 10); ctx.lineTo(x + 14, yy + 20); ctx.lineTo(x + 42, yy + 33); ctx.stroke();
      }
    } else if (this.config.world.id === 'oz') {
      for (let yy = y + 20; yy < y + height; yy += 30) { ctx.beginPath(); ctx.moveTo(x, yy); ctx.lineTo(x + width, yy); ctx.stroke(); }
    } else if (this.config.world.id === 'pinocchio') {
      for (let xx = x + 12; xx < x + width; xx += 22) { ctx.beginPath(); ctx.moveTo(xx, y); ctx.lineTo(xx, y + height); ctx.stroke(); }
    } else {
      ctx.beginPath(); ctx.arc(x + width / 2, y + 23, Math.min(15, width / 4), 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
  }

  private drawFall(): void {
    const ctx = this.ctx;
    for (const platform of this.game.platforms as FallPlatform[]) {
      ctx.fillStyle = 'rgba(8,14,26,.34)';
      ctx.beginPath(); ctx.roundRect(platform.x - 4, platform.y + 5, platform.width + 8, 13, 7); ctx.fill();
      ctx.fillStyle = platform.moving ? '#b7d7c3' : this.config.world.palette[2];
      ctx.beginPath(); ctx.roundRect(platform.x, platform.y, platform.width, 9, 5); ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.58)'; ctx.fillRect(platform.x + 10, platform.y + 2, platform.width - 20, 2);
      if (platform.page) this.drawPage(platform.x + platform.width / 2, platform.y - 15, 9);
    }
    for (const hazard of this.game.hazards as { x: number; y: number; r: number }[]) {
      this.drawHazard(hazard.x, hazard.y, hazard.r);
    }
    const player = this.game.player as { x: number; y: number };
    this.drawHero(player.x, player.y, this.config.character, 17, 0);
    ctx.fillStyle = 'rgba(255,255,255,.8)';
    ctx.font = '700 13px system-ui'; ctx.textAlign = 'left';
    ctx.fillText(`HEIGHT ${Math.floor(Number(this.game.height))}`, 28, 55);
  }

  private drawSwipe(): void {
    const ctx = this.ctx;
    for (const object of this.game.objects as SwipeObject[]) {
      ctx.save();
      ctx.translate(object.x, object.y);
      ctx.rotate(Math.sin(this.elapsed * 2 + object.x) * 0.12);
      if (object.kind === 'page') {
        ctx.fillStyle = object.color;
        ctx.beginPath(); ctx.moveTo(0, -object.r); ctx.lineTo(object.r * 0.7, 0); ctx.lineTo(0, object.r); ctx.lineTo(-object.r * 0.7, 0); ctx.closePath(); ctx.fill();
      } else {
        const image = object.kind === 'hazard' ? this.swipeHazardImage : this.swipeTargetImage;
        if (image?.complete && image.naturalWidth > 0) {
          const size = object.r * 2.45;
          const ratio = image.naturalWidth / image.naturalHeight;
          const width = ratio > 1 ? size : size * ratio;
          const height = ratio > 1 ? size / ratio : size;
          ctx.drawImage(image, -width / 2, -height / 2, width, height);
        } else {
          ctx.fillStyle = object.color;
          ctx.beginPath(); ctx.arc(0, 0, object.r, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = 'rgba(40,40,55,.76)'; ctx.font = '700 10px system-ui'; ctx.textAlign = 'center'; ctx.fillText(this.shortLabel(object.label), 0, 4);
        }
      }
      ctx.restore();
    }
    ctx.fillStyle = 'rgba(18,20,33,.3)';
    ctx.fillRect(0, H - 62, W, 62);
  }

  private drawCrossing(): void {
    const ctx = this.ctx;
    const lanes = this.game.lanes as Lane[];
    for (let row = 0; row < lanes.length; row += 1) {
      const lane = lanes[row];
      const y = 104 + row * 48;
      ctx.fillStyle = lane.safe ? 'rgba(42,58,68,.36)' : (row % 2 ? 'rgba(17,23,39,.62)' : 'rgba(27,36,53,.64)');
      ctx.fillRect(22, y - 21, W - 44, 44);
      if (!lane.safe) {
        ctx.strokeStyle = 'rgba(244,222,171,.21)'; ctx.setLineDash([9, 9]); ctx.beginPath(); ctx.moveTo(24, y + 21); ctx.lineTo(W - 24, y + 21); ctx.stroke(); ctx.setLineDash([]);
      }
      for (const obstacle of lane.obstacles) this.drawMovingHazard(obstacle.x, y, obstacle.width);
      if (lane.pageX !== undefined) this.drawPage(lane.pageX, y, 10);
    }
    const player = this.game.player as { col: number; row: number };
    const px = 39 + player.col * 44.5;
    const py = 104 + player.row * 48;
    this.drawHero(px, py, this.config.character, 16, 0);
    ctx.fillStyle = 'rgba(255,255,255,.8)'; ctx.font = '700 13px system-ui'; ctx.textAlign = 'left';
    ctx.fillText(`DISTANCE ${this.score}`, 28, 58);
  }

  private drawMovingHazard(x: number, y: number, width: number): void {
    const ctx = this.ctx;
    ctx.fillStyle = this.config.world.palette[2];
    ctx.globalAlpha = 0.82;
    ctx.beginPath(); ctx.roundRect(x, y - 12, width, 24, 8); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(24,28,43,.55)';
    ctx.beginPath(); ctx.arc(x + 9, y + 10, 4, 0, Math.PI * 2); ctx.arc(x + width - 9, y + 10, 4, 0, Math.PI * 2); ctx.fill();
  }

  private drawHero(x: number, y: number, character: Character, radius: number, tilt: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(tilt);
    ctx.fillStyle = 'rgba(11,16,28,.35)';
    ctx.beginPath(); ctx.ellipse(0, radius + 6, radius * .9, 4, 0, 0, Math.PI * 2); ctx.fill();
    const frame = characterAtlasFrame(character.id);
    const drawWidth = radius * 2.1;
    const drawHeight = drawWidth * frame.height / frame.width;
    if (this.characterAtlas?.complete && this.characterAtlas.naturalWidth > 0) {
      const atlas = this.characterAtlas;
      ctx.shadowColor = 'rgba(5, 9, 18, .55)';
      ctx.shadowBlur = 7;
      ctx.drawImage(
        atlas,
        frame.x + 2, frame.y + 2, frame.width - 4, frame.height - 4,
        -drawWidth / 2, -drawHeight * 0.48, drawWidth, drawHeight,
      );
      ctx.shadowBlur = 0;
    } else {
      ctx.fillStyle = character.coat;
      ctx.beginPath(); ctx.ellipse(0, radius * .56, radius * .92, radius * .94, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = character.skin;
      ctx.beginPath(); ctx.arc(0, -radius * .38, radius * .76, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = character.hair;
      ctx.beginPath(); ctx.arc(0, -radius * .66, radius * .78, Math.PI, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  private drawHazard(x: number, y: number, r: number): void {
    const ctx = this.ctx;
    ctx.fillStyle = '#e57a78';
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#4e293d'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x - 7, y - 7); ctx.lineTo(x + 7, y + 7); ctx.moveTo(x + 7, y - 7); ctx.lineTo(x - 7, y + 7); ctx.stroke();
  }

  private drawPage(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI / 4);
    ctx.fillStyle = '#f2d276'; ctx.shadowColor = '#ffe7a5'; ctx.shadowBlur = 12;
    ctx.beginPath(); ctx.roundRect(-size * .62, -size * .62, size * 1.24, size * 1.24, 3); ctx.fill();
    ctx.shadowBlur = 0; ctx.fillStyle = 'rgba(105,78,41,.55)'; ctx.fillRect(-2, -size * .35, 3, size * .7);
    ctx.restore();
  }

  private drawStar(x: number, y: number, size: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    for (let i = 0; i < 8; i += 1) {
      const angle = Math.PI * i / 4 - Math.PI / 2;
      const radius = i % 2 === 0 ? size : size * 0.36;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath(); ctx.fill();
  }

  private drawParticles(): void {
    const ctx = this.ctx;
    for (const particle of this.particles) {
      ctx.globalAlpha = clamp(particle.life * 2, 0, 1);
      ctx.fillStyle = particle.color;
      ctx.beginPath(); ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  private drawTrail(): void {
    const ctx = this.ctx;
    if (this.swipeTrail.length < 2) return;
    ctx.lineCap = 'round';
    for (let i = 1; i < this.swipeTrail.length; i += 1) {
      const a = this.swipeTrail[i - 1]; const b = this.swipeTrail[i];
      ctx.strokeStyle = `rgba(255,239,186,${clamp(b.life / .24, 0, 0.75)})`;
      ctx.lineWidth = 3 + b.life * 8;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
  }

  private shortLabel(value: string): string {
    return value.split(' ').map((word) => word[0]).join('').slice(0, 2).toUpperCase();
  }
}
